import { createTheme } from '@mui/material/styles'
import {
  BASE_COLORS,
  ERROR_COLORS,
  GRAY_COLORS,
  INFO_COLORS,
  PRIMARY_COLORS,
  SUCCESS_COLORS,
  WARNING_COLORS
} from 'asterX-UI/colors.constant'
import { CheckBoxIcon, CheckBoxOutlineBlankIcon } from 'asterX-UI/Icons'
import React from 'react'

const paddingSmallBtn = '10px 14px'
const paddingLargeBtn = '12px 20px'
const paddingMediumBtn = '8px 12px'

export const MODAL_STYLES = {
  variant: ['info', 'warning', 'error', 'success'],
  default: {
    width: { desktop: 400, mobile: 343 }
  }
}

const breakpointsValues = {
  xs: 10,
  sm: 450,
  md: 768,
  lg: 1024,
  xl: 1280
}

export const COLOR_VARIANTS = ['primary', 'secondary', 'info', 'warning', 'error', 'success', 'black']

export const asterTheme = createTheme({
  components: {
    MuiInput: {
      disableUnderline: true
    },
    MuiSelect: {
      MenuProps: {
        getContentAnchorEl: null,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      }
    },
    MuiCheckbox: {
      icon: <CheckBoxOutlineBlankIcon />,
      checkedIcon: <CheckBoxIcon />
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            margin: '0px'
          },
          '&::before': {
            display: 'none'
          }
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: 'auto'
          }
        },
        content: {
          '&.Mui-expanded': {
            margin: '0px',
            minHeight: 'auto'
          }
        }
      }
    },
    MuiTextField: {
      InputLabelProps: {
        shrink: true
      },
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            padding: '10px 14px',
            fontSize: '14px',
            height: 'auto'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        grayText: {
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          border: `1px solid ${GRAY_COLORS[300]}`,
          color: GRAY_COLORS[700],
          fontSize: 14,
          padding: '8px 14px'
        }
      }
    }
  },
  breakpoints: {
    values: breakpointsValues
  },
  palette: {
    primary: {
      light: INFO_COLORS[50],
      main: INFO_COLORS[600],
      dark: INFO_COLORS[700],
      contrastText: BASE_COLORS.White
    },
    secondary: {
      light: ERROR_COLORS[50],
      main: ERROR_COLORS[600],
      dark: ERROR_COLORS[700],
      contrastText: BASE_COLORS.White
    },
    black: {
      light: GRAY_COLORS[50],
      main: GRAY_COLORS[800],
      dark: GRAY_COLORS[900],
      contrastText: BASE_COLORS.White
    },
    error: {
      light: ERROR_COLORS[50],
      main: ERROR_COLORS[600],
      dark: ERROR_COLORS[700],
      contrastText: BASE_COLORS.White
    },
    warning: {
      light: WARNING_COLORS[50],
      main: WARNING_COLORS[600],
      dark: WARNING_COLORS[700],
      contrastText: BASE_COLORS.White
    },
    success: {
      light: SUCCESS_COLORS[50],
      main: SUCCESS_COLORS[600],
      dark: SUCCESS_COLORS[700],
      contrastText: BASE_COLORS.White
    },
    info: {
      light: INFO_COLORS[50],
      main: INFO_COLORS[600],
      dark: INFO_COLORS[700],
      contrastText: BASE_COLORS.White
    },
    neutral: {
      light: PRIMARY_COLORS[50],
      main: PRIMARY_COLORS[50],
      dark: PRIMARY_COLORS[100],
      contrastText: PRIMARY_COLORS[700]
    },
    allPrimary: PRIMARY_COLORS,
    allError: ERROR_COLORS,
    allInfo: INFO_COLORS,
    allSuccess: SUCCESS_COLORS,
    allWarning: WARNING_COLORS,
    grey: GRAY_COLORS
  },
  typography: {
    fontFamily: 'Inter',
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontFamily: 'Inter',
      fontSize: 72,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontFamily: 'Inter',
      fontSize: 60,
      letterSpacing: '-0.02em'
    },
    h3: {
      fontFamily: 'Inter',
      fontSize: 48,
      letterSpacing: '-0.02em'
    },
    h4: {
      fontFamily: 'Inter',
      fontSize: 36,
      fontWeight: 600,
      letterSpacing: '-0.02em'
    },
    h5: {
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: 30
    },
    h6: {
      fontFamily: 'Inter',
      fontSize: 24
    },
    subtitle1: {
      fontFamily: 'Inter',
      fontSize: 18,
      fontWeight: 600,
      color: GRAY_COLORS[900]
    },
    subtitle2: {
      fontFamily: 'Inter',
      fontSize: 16,
      fontWeight: 500,
      color: GRAY_COLORS[900]
    },
    body1: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: 16,
      color: GRAY_COLORS[900]
    },
    body2: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: 14,
      color: GRAY_COLORS[900]
    },
    small: {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: 12,
      color: GRAY_COLORS[600]
    },
    button: {
      fontFamily: 'Inter',
      textTransform: 'none',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: 16
    },
    caption: { fontFamily: 'Inter' }
  },
  spacing: 4,
  shadows: [
    'none',
    '0px 1px 2px rgba(16, 24, 40, 0.02)',
    '0px 1px 2px rgba(16, 24, 40, 0.02)',
    '0px 1px 2px rgba(16, 24, 40, 0.02)',
    '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
    '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
    '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
    '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
    '0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)',
    '0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)',
    '0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)',
    '0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)',
    '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
    '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
    '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
    '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
    '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
    '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
    '0px 32px 64px -12px rgba(16, 24, 40, 0.14)',
    '0px 32px 64px -12px rgba(16, 24, 40, 0.14)',
    '0px 32px 64px -12px rgba(16, 24, 40, 0.14)'
  ],
  shape: {
    borderRadius: 8
  },
  overrides: {
    MuiBackdrop: {
      root: {
        backdropFilter: 'blur(8px)'
      }
    },
    MuiDialogContent: {
      root: {
        padding: 12
      }
    },
    MuiDialog: {
      paper: {
        borderRadius: 12,
        padding: 4,
        margin: 16
      },
      paperFullWidth: {
        width: '100%'
      },
      paperWidthXs: {
        maxWidth: MODAL_STYLES.default.width.mobile,
        '&$paperScrollBody': {
          maxWidth: MODAL_STYLES.default.width.mobile
        }
      },
      paperWidthSm: {
        maxWidth: MODAL_STYLES.default.width.desktop,
        '&$paperScrollBody': {
          maxWidth: MODAL_STYLES.default.width.desktop
        }
      }
    },
    MuiPagination: {
      root: {
        [createTheme({ breakpoints: { values: breakpointsValues } }).breakpoints.down('md')]: {
          border: 'none'
        }
      }
    },
    MuiButton: {
      root: {
        padding: paddingMediumBtn,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        '&:hover': {
          backgroundColor: GRAY_COLORS[50],
          color: GRAY_COLORS[700],
          '@media (hover: none)': {
            backgroundColor: GRAY_COLORS[50],
            color: GRAY_COLORS[700]
          }
        }
      },
      label: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8
      },
      outlined: {
        padding: paddingMediumBtn,
        color: GRAY_COLORS[700],
        backgroundColor: BASE_COLORS.White,
        border: `1px solid ${GRAY_COLORS[300]}`,
        '&:hover': {
          backgroundColor: GRAY_COLORS[50],
          color: GRAY_COLORS[800],
          border: `1px solid ${GRAY_COLORS[300]}`,
          '@media (hover: none)': {
            backgroundColor: GRAY_COLORS[50],
            color: GRAY_COLORS[800],
            border: `1px solid ${GRAY_COLORS[300]}`
          }
        },
        '&$focusVisible': {
          border: `1px solid ${GRAY_COLORS[300]}`,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #F2F4F7'
        },
        '&$disabled': {
          backgroundColor: BASE_COLORS.White,
          color: GRAY_COLORS[400],
          border: `1px solid ${GRAY_COLORS[200]}`,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)'
        }
      },
      contained: {
        padding: paddingMediumBtn,
        color: PRIMARY_COLORS[700],
        backgroundColor: PRIMARY_COLORS[50],
        '&:hover': {
          backgroundColor: PRIMARY_COLORS[100],
          color: PRIMARY_COLORS[800],
          '@media (hover: none)': {
            color: PRIMARY_COLORS[800],
            backgroundColor: PRIMARY_COLORS[100]
          }
        },
        '&$focusVisible': {
          border: `1px solid ${PRIMARY_COLORS[50]}`,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #F4EBFF'
        },
        '&$disabled': {
          backgroundColor: PRIMARY_COLORS[25],
          color: PRIMARY_COLORS[300],
          border: `1px solid ${PRIMARY_COLORS[25]}`
        }
      },
      text: {
        color: GRAY_COLORS[600],
        padding: paddingMediumBtn,
        '&:hover': {
          backgroundColor: '#F9FAFB73',
          color: GRAY_COLORS[700],
          '@media (hover: none)': {
            backgroundColor: '#F9FAFB73',
            color: GRAY_COLORS[700]
          }
        },
        '&$focusVisible': {
          color: GRAY_COLORS[600]
        },
        '&$disabled': {
          color: GRAY_COLORS[300]
        }
      },
      textPrimary: {
        '&$disabled': {
          color: GRAY_COLORS[300]
        }
      },
      containedPrimary: {
        '&:hover': {
          color: BASE_COLORS.White,
          '@media (hover: none)': {
            color: BASE_COLORS.White
          }
        },
        '&$focusVisible': {
          border: `1px solid ${PRIMARY_COLORS[600]}`,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #F4EBFF'
        },
        '&$disabled': {
          backgroundColor: INFO_COLORS[200],
          color: BASE_COLORS.White,
          border: `1px solid ${INFO_COLORS[200]}`
        }
      },
      containedSecondary: {
        '&:hover': {
          color: BASE_COLORS.White,
          '@media (hover: none)': {
            color: BASE_COLORS.White
          }
        },
        '&$focusVisible': {
          border: `1px solid ${ERROR_COLORS[600]}`,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #FEE4E2'
        },
        '&$disabled': {
          backgroundColor: ERROR_COLORS[200],
          color: BASE_COLORS.White,
          border: `1px solid ${ERROR_COLORS[200]}`
        }
      },
      sizeSmall: {
        fontWeight: 600,
        padding: paddingSmallBtn
      },
      sizeLarge: {
        padding: paddingLargeBtn
      },
      textSizeSmall: {
        padding: paddingSmallBtn
      },
      textSizeLarge: {
        padding: paddingLargeBtn
      },
      outlinedSizeSmall: {
        padding: paddingSmallBtn
      },
      outlinedSizeLarge: {
        padding: paddingLargeBtn
      },
      containedSizeSmall: {
        padding: paddingSmallBtn
      },
      containedSizeLarge: {
        padding: paddingLargeBtn
      }
    },
    MuiFormLabel: {
      root: {
        fontSize: 14,
        fontStyle: 'normal',
        fontFamily: 'Inter',
        fontWeight: 500,
        lineHeight: '20px',
        color: GRAY_COLORS[700],

        '&.MuiInputLabel-shrink': {
          transform: 'translate(0, 1.5px) scale(1)'
        }
      }
    },
    MuiCheckbox: {
      colorPrimary: {
        color: BASE_COLORS.White,
        borderRadius: '35%',
        '&:hover': {
          backgroundColor: PRIMARY_COLORS[100],
          '@media (hover: none)': {
            backgroundColor: 'transparent'
          }
        },
        '&$checked': {
          color: INFO_COLORS[600],
          '&:hover': {
            backgroundColor: INFO_COLORS[100],
            '@media (hover: none)': {
              backgroundColor: 'transparent'
            }
          }
        },
        '&$disabled': {
          color: GRAY_COLORS[300]
        }
      }
    },
    MuiChip: {
      root: {
        backgroundColor: GRAY_COLORS[100],
        [createTheme({ breakpoints: { values: breakpointsValues } }).breakpoints.down('md')]: {
          height: 26
        }
      }
    },
    MuiSelect: {
      selectMenu: {
        height: '24px!important'
      }
    },
    MuiInput: {
      root: {
        fontSize: 16,
        color: GRAY_COLORS[900],
        borderRadius: 8,
        backgroundColor: BASE_COLORS.White,
        border: `1px solid ${GRAY_COLORS[300]}`,
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
        '&$focused': {
          border: `1px solid ${INFO_COLORS[300]}`,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 2px #d2dffa'
        },
        '&$error': {
          border: `1px solid ${ERROR_COLORS[300]}`,
          transform: 'scaleX(1)' // error is always underlined in red
        },
        '&$disabled': {
          backgroundColor: GRAY_COLORS[50],
          border: `1px solid ${GRAY_COLORS[300]}`,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)'
        }
      },
      formControl: {
        'label + &': {
          marginTop: 30
        }
      },
      input: {
        padding: '10px 14px',
        fontSize: 16,
        height: 24,
        '&:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px #E0E7FF inset',
          WebkitTextFillColor: INFO_COLORS[600],
          caretColor: INFO_COLORS[600],
          borderRadius: 'inherit'
        }
      }
    },
    MuiTableContainer: {
      root: {
        borderRadius: 12,
        border: `1px solid ${GRAY_COLORS[200]}`,
        backgroundColor: BASE_COLORS.White,
        boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)'
      }
    },
    MuiTableHead: {
      root: {
        backgroundColor: GRAY_COLORS[50]
      }
    },
    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${GRAY_COLORS[200]}`
      }
    },
    MuiAutocomplete: {
      inputRoot: {
        '&[class*="MuiInput-root"]': {
          paddingBottom: 1,
          '& $input': {
            padding: '10px 14px'
          },
          '& $input:first-child': {
            padding: '10px 14px'
          }
        }
      }
    }
  }
})
